import { Link } from 'react-router';
import { formatNumberWithCommasAndDecimals } from '@rocco/utils/format-number';
import type { SessionSchemaShape } from '~/modules/root/util/session.server';
import type { FilterStates } from '~/modules-rocco/filters/types/filter-states';
import { generateURL } from '~/modules-rocco/url/api/generate-url';
import type { SearchIndex } from '../type/api';
import {
    getSearchCategoryFromIndex,
    getSearchCategoryLabelFromIndex,
} from './search-categories';

/**
 * Generate the messages to show to the user when the filters are auto-generated
 * for the search query.
 * @returns The messages to show to the user if they should be shown.
 */
export function getAutoFilterMessage({
    autoSearchFilters,
    searchIndex,
    filterStates,
    totalCount,
}: {
    autoSearchFilters: SessionSchemaShape['searchFilters'] | null;
    searchIndex: SearchIndex | undefined;
    filterStates: FilterStates;
    totalCount: number;
}) {
    if (!autoSearchFilters) {
        return null;
    }
    const category = getSearchCategoryFromIndex(searchIndex);
    if (
        !autoSearchFilters.modifiedQuery &&
        (!autoSearchFilters.category || autoSearchFilters.category !== category)
    ) {
        // No message if no auto-filters or the applied filters do not match the auto-filters.
        return null;
    }
    const originalSearchUrl = generateURL('/search', {
        search: autoSearchFilters.originalQuery,
    });
    const categoryName =
        getSearchCategoryLabelFromIndex(searchIndex)?.toLowerCase();
    return {
        message: (
            <>
                {`Showing ${formatNumberWithCommasAndDecimals(totalCount)} results`}
                {!!categoryName && (
                    <>
                        {' in '}
                        <strong>{categoryName}</strong>
                    </>
                )}
                <>
                    {' for '}
                    <strong>
                        {autoSearchFilters.modifiedQuery ||
                            autoSearchFilters.originalQuery}
                    </strong>
                </>
                {showColorMessage(autoSearchFilters.color, filterStates) && (
                    <>
                        {' with the colour '}
                        <strong>{autoSearchFilters.color}</strong>
                    </>
                )}
            </>
        ),
        suggestion: (
            <>
                Search instead for{' '}
                <Link className="text-brick-900" to={originalSearchUrl}>
                    all results
                    {!!autoSearchFilters.modifiedQuery &&
                        ` containing “${autoSearchFilters.originalQuery}”`}
                </Link>
            </>
        ),
    };
}

/**
 * Check if the auto filter message should mention the color filter. It should
 * mention the color filter if the applied color filter matches the auto-filter.
 * @param color The color to check.
 * @param filterStates The filter states.
 * @returns The auto filter message should mention the color filter.
 */
function showColorMessage(
    color: string | null | undefined,
    filterStates: FilterStates,
) {
    if (!color) {
        return false;
    }
    const colorFilterState = filterStates.states.colors;
    if (!colorFilterState) {
        return false;
    }
    if (colorFilterState.selectedItems.length !== 1) {
        return false;
    }
    return colorFilterState.selectedItems[0]?.value === color;
}
